import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';

import Breadcrumb from '@/components/breadcrumb';
import { KIT_BOX_SKU, KIT_CONTROL_SKU } from '@/utils/purchase-order-item';

import { SerialCheckBanner } from './serial-check-banner';
import { useReception } from './use-reception';

const formatDate = (value: string) => format(new Date(value), 'dd/MM/yyyy HH:mm');

export const ReceptionSection = () => {
  const hooks = useReception();

  const isBusy = hooks.isLoadingPo || hooks.isValidating;
  const isWithoutPoBusy = hooks.isCheckingWithoutPo;

  const kitPairColumns: GridColDef[] = [
    { field: 'principalSerial', headerName: 'Serie caja', flex: 1, minWidth: 180 },
    { field: 'complementSerial', headerName: 'Serie control', flex: 1, minWidth: 180 },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 170,
      renderCell: (params: GridRenderCellParams) => formatDate(params.value as string),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          color="error"
          size="small"
          onClick={() => hooks.onDeleteKitPair(params.row.id as string)}
          disabled={hooks.isLoadingKitPairs}
        >
          Eliminar
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6 flex flex-col gap-4 pb-12">
      <Breadcrumb
        sections={[{ label: 'Izzi' }, { label: 'Escaneo' }]}
        description="Con PO se recepciona inventario. Sin PO se valida cuarentena o se relaciona kit."
      />

      <div className="w-full max-w-5xl mx-auto flex flex-col gap-6">
        <Card variant="outlined">
          <CardContent className="flex flex-col gap-6">
            <Tabs
              value={hooks.scanTab}
              onChange={hooks.onScanTabChange}
              sx={{ borderBottom: '1px solid #f3f4f6' }}
            >
              <Tab label="Con orden de compra" />
              <Tab label="Sin orden de compra" />
            </Tabs>

            {hooks.scanTab === 0 && (
              <>
                <Typography variant="subtitle1" fontWeight="bold">
                  Recepción de inventario
                </Typography>

                <div className="flex gap-4 items-start">
                  <TextField
                    label="Identificador de PO"
                    variant="outlined"
                    fullWidth
                    required
                    value={hooks.poId}
                    onChange={hooks.onChangePoId}
                    onKeyDown={hooks.onKeyDownPoId}
                    onBlur={hooks.onBlurPoId}
                    placeholder="Ingrese el identificador de PO"
                    disabled={isBusy}
                    autoFocus
                  />
                  <Button
                    variant="contained"
                    onClick={hooks.onLoadPo}
                    disabled={isBusy || !hooks.poId.trim()}
                    sx={{ minWidth: 130, height: 56, mt: 0 }}
                  >
                    {hooks.isLoadingPo ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      'Cargar PO'
                    )}
                  </Button>
                </div>

                <TextField
                  select
                  label="SKU"
                  variant="outlined"
                  fullWidth
                  required
                  value={hooks.sku || ''}
                  onChange={hooks.onChangeSku}
                  disabled={isBusy || !hooks.isPoLoaded}
                  helperText={
                    hooks.isLoadingPo
                      ? 'Cargando productos de la PO...'
                      : hooks.isPoLoaded && hooks.poItems.length === 0
                        ? 'La PO no tiene productos disponibles'
                        : hooks.isKitMode
                          ? 'Puedes escanear solo la caja o vincular pareja más abajo (opcional)'
                          : undefined
                  }
                >
                  <MenuItem value="" disabled>
                    Seleccione un SKU
                  </MenuItem>
                  {hooks.poItems.map((item) => (
                    <MenuItem key={item.sku} value={item.sku}>
                      {item.productName} ({item.sku}) — {item.scannedCount}/{item.quantity}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Número de serie"
                  variant="outlined"
                  fullWidth
                  required
                  value={hooks.serialNumber}
                  onChange={hooks.onChangeSerialNumber}
                  onKeyDown={hooks.onKeyDownSerialNumber}
                  placeholder="Escanee o ingrese el número de serie"
                  disabled={!hooks.canScanSingle}
                  inputRef={hooks.serialInputRef}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <QrCodeScannerIcon color={hooks.canScanSingle ? 'primary' : 'disabled'} />
                      </InputAdornment>
                    ),
                    endAdornment:
                      hooks.isValidating && !hooks.validatedBoxSerial ? (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                />

                {hooks.isKitMode && (
                  <div className="flex flex-col gap-4 border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                    <Typography variant="subtitle2" fontWeight="bold">
                      Vincular pareja kit (opcional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Usa esta sección solo si deseas registrar la relación caja + control. No es
                      obligatorio para recepcionar la caja.
                    </Typography>

                    <TextField
                      label={`Serie caja (${KIT_BOX_SKU})`}
                      variant="outlined"
                      fullWidth
                      value={hooks.boxSerial}
                      onChange={hooks.onChangeBoxSerial}
                      onKeyDown={hooks.onKeyDownBoxSerial}
                      placeholder="Escanee la serie de la caja"
                      disabled={!hooks.canScanOptionalBox}
                      inputRef={hooks.boxInputRef}
                      helperText={
                        hooks.validatedBoxSerial
                          ? `Caja validada: ${hooks.validatedBoxSerial}. Escanea el control para vincular.`
                          : 'Opcional: valida la caja antes de escanear el control'
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QrCodeScannerIcon
                              color={hooks.canScanOptionalBox ? 'primary' : 'disabled'}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label={`Serie control (${KIT_CONTROL_SKU})`}
                      variant="outlined"
                      fullWidth
                      value={hooks.controlSerial}
                      onChange={hooks.onChangeControlSerial}
                      onKeyDown={hooks.onKeyDownControlSerial}
                      placeholder="Escanee la serie del control"
                      disabled={!hooks.canScanOptionalControl}
                      inputRef={hooks.controlInputRef}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QrCodeScannerIcon
                              color={hooks.canScanOptionalControl ? 'primary' : 'disabled'}
                            />
                          </InputAdornment>
                        ),
                        endAdornment:
                          hooks.isValidating && hooks.validatedBoxSerial ? (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ) : undefined,
                      }}
                    />
                  </div>
                )}
              </>
            )}

            {hooks.scanTab === 1 && (
              <>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <Typography variant="subtitle2" fontWeight="bold">
                    Sin PO no se recepciona
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Solo se valida si la serie se puede usar y, si aplica, se relaciona el kit. No
                    entra a inventario.
                  </Typography>
                </div>

                <ToggleButtonGroup
                  exclusive
                  value={hooks.withoutPoMode}
                  onChange={hooks.onWithoutPoModeChange}
                  size="small"
                >
                  <ToggleButton value="validate">Validar serie</ToggleButton>
                  <ToggleButton value="kit">Relacionar kit</ToggleButton>
                </ToggleButtonGroup>

                {hooks.withoutPoMode === 'validate' && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Antes de dar salida o despachar, escanea la serie. Si está en cuarentena, no
                      la uses.
                    </Typography>
                    <div className="flex gap-3 items-start flex-wrap">
                      <TextField
                        label="Número de serie"
                        variant="outlined"
                        value={hooks.withoutPoSerial}
                        onChange={hooks.onChangeWithoutPoSerial}
                        onKeyDown={hooks.onKeyDownWithoutPoSerial}
                        placeholder="Escanea o ingresa la serie"
                        disabled={!hooks.canScanWithoutPo}
                        inputRef={hooks.withoutPoSerialRef}
                        sx={{ minWidth: 320, flex: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <QrCodeScannerIcon
                                color={hooks.canScanWithoutPo ? 'primary' : 'disabled'}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: isWithoutPoBusy ? (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={hooks.onCheckWithoutPoSerial}
                        disabled={!hooks.canScanWithoutPo || !hooks.withoutPoSerial.trim()}
                        sx={{ height: 56, minWidth: 120 }}
                      >
                        Validar
                      </Button>
                      {hooks.withoutPoCheck && (
                        <Button
                          variant="outlined"
                          color="inherit"
                          onClick={hooks.onClearWithoutPoCheck}
                          disabled={isWithoutPoBusy}
                          sx={{ height: 56 }}
                        >
                          Limpiar
                        </Button>
                      )}
                    </div>
                    {hooks.withoutPoCheck && <SerialCheckBanner check={hooks.withoutPoCheck} />}
                  </>
                )}

                {hooks.withoutPoMode === 'kit' && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Traspaso u otro movimiento sin PO. Valida caja y control por cuarentena y
                      guarda la pareja. No cuenta inventario.
                    </Typography>
                    <TextField
                      label={`Serie caja (${KIT_BOX_SKU})`}
                      variant="outlined"
                      fullWidth
                      value={hooks.withoutPoBoxSerial}
                      onChange={hooks.onChangeWithoutPoBoxSerial}
                      onKeyDown={hooks.onKeyDownWithoutPoBox}
                      placeholder="Escanee la serie de la caja"
                      disabled={!hooks.canScanWithoutPoBox}
                      inputRef={hooks.withoutPoBoxRef}
                      helperText={
                        hooks.validatedWithoutPoBox
                          ? `Caja validada: ${hooks.validatedWithoutPoBox}. Escanea el control.`
                          : 'Primero la caja, luego el control'
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QrCodeScannerIcon
                              color={hooks.canScanWithoutPoBox ? 'primary' : 'disabled'}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: isWithoutPoBusy && !hooks.validatedWithoutPoBox ? (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                    {hooks.withoutPoBoxCheck && (
                      <SerialCheckBanner check={hooks.withoutPoBoxCheck} label="Caja" />
                    )}
                    <TextField
                      label={`Serie control (${KIT_CONTROL_SKU})`}
                      variant="outlined"
                      fullWidth
                      value={hooks.withoutPoControlSerial}
                      onChange={hooks.onChangeWithoutPoControlSerial}
                      onKeyDown={hooks.onKeyDownWithoutPoControl}
                      placeholder="Escanee la serie del control"
                      disabled={!hooks.canScanWithoutPoControl}
                      inputRef={hooks.withoutPoControlRef}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QrCodeScannerIcon
                              color={hooks.canScanWithoutPoControl ? 'primary' : 'disabled'}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: isWithoutPoBusy && hooks.validatedWithoutPoBox ? (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                    {hooks.withoutPoControlCheck && (
                      <SerialCheckBanner check={hooks.withoutPoControlCheck} label="Control" />
                    )}
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent className="flex flex-col gap-4">
            <Typography variant="subtitle1" fontWeight="bold">
              Parejas de kit registradas
            </Typography>

            <div className="flex gap-3 items-center flex-wrap">
              <TextField
                label="Filtrar por serie caja"
                variant="outlined"
                size="small"
                value={hooks.filterPrincipalSerial}
                onChange={hooks.onChangeFilterPrincipalSerial}
                onKeyDown={hooks.onKeyDownKitFilter}
                sx={{ minWidth: 280 }}
              />
              <Button
                variant="contained"
                onClick={hooks.onSearchKitPairs}
                startIcon={<SearchIcon />}
                disabled={hooks.isLoadingKitPairs}
              >
                Buscar
              </Button>
              {hooks.appliedKitFilter && (
                <>
                  <Button variant="outlined" color="inherit" onClick={hooks.onClearKitFilter}>
                    Limpiar filtro
                  </Button>
                  <Chip
                    label={`Caja: ${hooks.appliedKitFilter}`}
                    size="small"
                    onDelete={hooks.onClearKitFilter}
                  />
                </>
              )}
            </div>

            <div className="bg-white rounded-2xl" style={{ height: 420, width: '100%' }}>
              <DataGrid
                rows={hooks.kitPairs}
                columns={kitPairColumns}
                loading={hooks.isLoadingKitPairs}
                disableRowSelectionOnClick
                paginationMode="server"
                rowCount={hooks.kitPairsTotal}
                paginationModel={hooks.kitPaginationModel}
                onPaginationModelChange={hooks.onChangeKitPaginationModel}
                pageSizeOptions={[10, 20, 50]}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
                  '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #e5e7eb' },
                  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                }}
                localeText={{ noRowsLabel: 'No hay parejas registradas.' }}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReceptionSection;
